"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { RefObject, useEffect, useRef, useState } from "react";

interface WebInteractionDemoProps {
  actionRef: RefObject<HTMLDivElement>;
  onDownloadClick?: () => void;
  onShowDownloadChange?: (show: boolean) => void;
}

export function WebInteractionDemo({ actionRef, onDownloadClick, onShowDownloadChange }: WebInteractionDemoProps) {
  const { scrollYProgress } = useScroll({
    target: actionRef,
    offset: ["start center", "end end"],
  });

  // 视频播放状态
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  // 基于滚动进度控制视频显示 - 第二阶段 (0.7-1.0, 占总进度30%)
  const showVideo = useTransform(scrollYProgress, [0.7, 0.75, 0.9, 1.0], [0, 1, 1, 1]);


  // 视频播放控制 - 基于 showVideo motion value
  useEffect(() => {
    const unsubscribe = showVideo.on("change", (value) => {
      const shouldPlay = value > 0;
      
      if (videoRef.current) {
        if (shouldPlay && !videoPlaying) {
          videoRef.current.currentTime = 0;
          videoRef.current.play();
          setVideoPlaying(true);
        } else if (!shouldPlay && videoPlaying) {
          videoRef.current.pause();
          setVideoPlaying(false);
        }
      }
    });

    return unsubscribe;
  }, [showVideo, videoPlaying]);

  // 文本覆盖层控制已转移到父组件


  // 浏览器整体出现动画 (0.1-0.3) - 扩展时间
  const browserOpacity = useTransform(scrollYProgress, [0.1, 0.3], [0, 1]);

  // Tab1 文章内容的出现动画 - 从左侧滑入 (0.1-0.28) - 扩展时间
  const articleTitleAppearX = useTransform(scrollYProgress, [0.1, 0.16], [-200, 0]);
  const articleTitleAppearOpacity = useTransform(scrollYProgress, [0.1, 0.16], [0, 1]);

  const articleParagraphAppearX = useTransform(scrollYProgress, [0.14, 0.2], [-200, 0]);
  const articleParagraphAppearOpacity = useTransform(scrollYProgress, [0.14, 0.2], [0, 1]);

  const articleImageAppearX = useTransform(scrollYProgress, [0.18, 0.24], [-200, 0]);
  const articleImageAppearOpacity = useTransform(scrollYProgress, [0.18, 0.24], [0, 1]);

  // Tab出现动画 (0.22-0.28) - 扩展时间
  const tabsAppearX = useTransform(scrollYProgress, [0.22, 0.28], [-200, 0]);
  const tabsAppearOpacity = useTransform(scrollYProgress, [0.22, 0.28], [0, 1]);

  // Tab1 文章内容的右侧退场动画 - 从0.35开始 (0.35-0.47) - 扩展时间并改为右移
  const articleTitleExitX = useTransform(scrollYProgress, [0.35, 0.39], [0, 200]);
  const articleTitleExitOpacity = useTransform(scrollYProgress, [0.35, 0.39], [1, 0]);

  const articleParagraphExitX = useTransform(scrollYProgress, [0.39, 0.43], [0, 200]);
  const articleParagraphExitOpacity = useTransform(scrollYProgress, [0.39, 0.43], [1, 0]);

  const articleImageExitX = useTransform(scrollYProgress, [0.43, 0.47], [0, 200]);
  const articleImageExitOpacity = useTransform(scrollYProgress, [0.43, 0.47], [1, 0]);

  // Tab1 右侧退场动画 (0.47-0.5)
  const tab1ExitX = useTransform(scrollYProgress, [0.47, 0.5], [0, 200]);
  const tab1ExitOpacity = useTransform(scrollYProgress, [0.47, 0.5], [1, 0]);

  // Tab切换到Tab2 (0.5-0.52) - 调整时机
  const tab2Opacity = useTransform(scrollYProgress, [0.5, 0.52], [0, 1]);

  // Tab2 表单内容的右侧退场动画 (0.58-0.68) - 扩展时间并改为右移
  const formTitleExitX = useTransform(scrollYProgress, [0.58, 0.6], [0, 200]);
  const formTitleExitOpacity = useTransform(scrollYProgress, [0.58, 0.6], [1, 0]);

  const formInput1ExitX = useTransform(scrollYProgress, [0.6, 0.62], [0, 200]);
  const formInput1ExitOpacity = useTransform(scrollYProgress, [0.6, 0.62], [1, 0]);

  const formInput2ExitX = useTransform(scrollYProgress, [0.62, 0.64], [0, 200]);
  const formInput2ExitOpacity = useTransform(scrollYProgress, [0.62, 0.64], [1, 0]);

  const formButtonExitX = useTransform(scrollYProgress, [0.64, 0.66], [0, 200]);
  const formButtonExitOpacity = useTransform(scrollYProgress, [0.64, 0.66], [1, 0]);

  // Tab2 右侧退场动画 (0.66-0.68)
  const tab2ExitX = useTransform(scrollYProgress, [0.66, 0.68], [0, 200]);
  const tab2ExitOpacity = useTransform(scrollYProgress, [0.66, 0.68], [1, 0]);

  // 组合X轴变换 - 将入场和退场的X轴动画合并
  const combinedTitleX = useTransform([articleTitleAppearX, articleTitleExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedParagraphX = useTransform([articleParagraphAppearX, articleParagraphExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedImageX = useTransform([articleImageAppearX, articleImageExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedTabsX = useTransform([tabsAppearX, tab1ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedTab2X = useTransform([tabsAppearX, tab2ExitX], ([appear, exit]) => (appear as number) + (exit as number));

  // 组合透明度变换
  const combinedTitleOpacity = useTransform([articleTitleAppearOpacity, articleTitleExitOpacity], ([appear, exit]) => Math.min(appear as number, exit as number));
  const combinedParagraphOpacity = useTransform([articleParagraphAppearOpacity, articleParagraphExitOpacity], ([appear, exit]) => Math.min(appear as number, exit as number));
  const combinedImageOpacity = useTransform([articleImageAppearOpacity, articleImageExitOpacity], ([appear, exit]) => Math.min(appear as number, exit as number));
  const combinedTab1Opacity = useTransform([tabsAppearOpacity, tab1ExitOpacity], ([appear, exit]) => Math.min(appear as number, exit as number));
  const combinedTab2Opacity = useTransform([tabsAppearOpacity, tab2ExitOpacity], ([appear, exit]) => Math.min(appear as number, exit as number));

  // 表单元素的组合透明度
  const combinedFormTitleOpacity = useTransform([tab2Opacity, formTitleExitOpacity], ([tab2, exit]) => Math.min(tab2 as number, exit as number));
  const combinedFormInput1Opacity = useTransform([tab2Opacity, formInput1ExitOpacity], ([tab2, exit]) => Math.min(tab2 as number, exit as number));
  const combinedFormInput2Opacity = useTransform([tab2Opacity, formInput2ExitOpacity], ([tab2, exit]) => Math.min(tab2 as number, exit as number));
  const combinedFormButtonOpacity = useTransform([tab2Opacity, formButtonExitOpacity], ([tab2, exit]) => Math.min(tab2 as number, exit as number));

  // 鼠标动画序列 - 扩展时间并改为右移退场
  // 1. 从左侧旋转出现 (0.28-0.3) - 调整时机
  const mouseAppearX = useTransform(scrollYProgress, [0.28, 0.3], [-100, 200]);
  const mouseAppearRotation = useTransform(scrollYProgress, [0.28, 0.3], [-45, 0]);
  const mouseAppearOpacity = useTransform(scrollYProgress, [0.28, 0.3], [0, 1]);

  // 2. 滚动时向下移动 (0.32-0.38) - 扩展时间
  const mouseScrollY = useTransform(scrollYProgress, [0.32, 0.38], [80, 150]);

  // 3. 点击切换Tab (0.48-0.52) - 调整时机
  const mouseTabX = useTransform(scrollYProgress, [0.48, 0.52], [200, 150]);
  const mouseTabY = useTransform(scrollYProgress, [0.48, 0.52], [150, 60]);

  // 4. 移动到Create Button (0.54-0.58) - 调整时机
  const mouseButtonX = useTransform(scrollYProgress, [0.54, 0.58], [150, 120]);
  const mouseButtonY = useTransform(scrollYProgress, [0.54, 0.58], [60, 280]);

  // 5. 右侧退场 (0.68-0.7) - 改为右移
  const mouseFinalX = useTransform(scrollYProgress, [0.68, 0.7], [0, 200]);
  const mouseFinalOpacity = useTransform(scrollYProgress, [0.68, 0.7], [1, 0]);

  // 滚动条动画 - 扩展时间并改为右移退场
  // 1. 在鼠标滚动时出现并激活 (0.32-0.38) - 调整时机
  const scrollbarAppearOpacity = useTransform(scrollYProgress, [0.32, 0.38], [0, 1]);
  const scrollbarActiveHeight = useTransform(scrollYProgress, [0.32, 0.38], [8, 20]);

  // 2. 滚动条右侧退场动画 (0.68-0.7) - 改为右移
  const scrollbarExitX = useTransform(scrollYProgress, [0.68, 0.7], [0, 200]);
  const scrollbarExitOpacity = useTransform(scrollYProgress, [0.68, 0.7], [1, 0]);

  // 交互动画 - 调整时机以适应新的时间轴
  // 1. 输入框打字动画 (0.56-0.58)，保持到退出
  const typingTextWidth = useTransform(scrollYProgress, [0.56, 0.58, 0.64], [0, 80, 80]);
  const typingCursorVisible = useTransform(scrollYProgress, [0.56, 0.58, 0.6], [1, 1, 0]);

  // 2. 按钮按下动画 (0.58-0.6) - 调整时机
  const buttonPressScale = useTransform(scrollYProgress, [0.58, 0.59, 0.6], [1, 0.95, 1]);

  // 3. 按钮点击指示器 (0.59-0.6) - 橙色
  const buttonClickIndicatorOpacity = useTransform(scrollYProgress, [0.59, 0.595, 0.6], [0, 1, 0]);

  // 4. 等待指示器已移除

  return (
    <div className="w-full relative flex items-center justify-center">
        {/* 视频容器 - 基于滚动进度显示 (第二阶段) */}
        <motion.div
          className="absolute inset-0 z-20 flex items-center justify-center"
          style={{ 
            opacity: showVideo,
            x: useTransform(showVideo, [0, 1], [-100, 0]),
            filter: useTransform(showVideo, [0, 1], ["blur(4px)", "blur(0px)"])
          }}
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover rounded-2xl"
            src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Browse+USE.webm"
            autoPlay
            loop
            muted
            playsInline
          />
        </motion.div>

        {/* Wireframe容器 */}
        <motion.div
          className="w-[700px] h-[500px] border-2 border-white/10 rounded-lg bg-white/10 relative overflow-hidden"
          style={{ 
            opacity: useTransform(showVideo, [0, 0.1, 0.5, 1], [1, 1, 0.3, 0])
          }}
        >
          {/* Browser Header */}
          <div className="flex items-center gap-2 p-4 border-b border-white/10">
            {/* Traffic Lights */}
            <div className="flex gap-2">
              <div className="w-3 h-3 rounded-full bg-white/10 border border-white/10"></div>
              <div className="w-3 h-3 rounded-full bg-white/10 border border-white/10"></div>
              <div className="w-3 h-3 rounded-full bg-white/10 border border-white/10"></div>
            </div>

            {/* Address Bar */}
            <div className="flex-1 ml-4">
              <div className="h-8 bg-white/5 border border-white/10 rounded px-3 flex items-center">
                <div className="w-4 h-4 border border-white/20 rounded-sm mr-2"></div>
                <div className="h-2 bg-white/20 rounded flex-1"></div>
              </div>
            </div>

            {/* Nav Buttons */}
            <div className="flex gap-2 ml-4">
              <div className="w-8 h-8 border border-white/20 rounded"></div>
              <div className="w-8 h-8 border border-white/20 rounded"></div>
            </div>
          </div>

          {/* Tab Bar */}
          <div className="bg-white/5 border-b border-white/10 flex px-4 py-1">
            {/* Tab 1 - Article */}
            <motion.div
              className="px-4 py-2 text-[10px] text-white/80 bg-white/10 rounded-t-lg border-r border-white/10 flex items-center gap-2"
              style={{
                x: combinedTabsX,
                opacity: useTransform(() => {
                  const combinedOpacity = combinedTab1Opacity.get();
                  const tab2Op = tab2Opacity.get();
                  return combinedOpacity * (1 - tab2Op);
                }),
              }}
            >
              <div className="w-2 h-2 bg-white/10 rounded-full border border-white/10"></div>
              <div className="h-2 bg-white/40 rounded w-8"></div>
            </motion.div>

            {/* Tab 2 - Form */}
            <motion.div
              className="px-4 py-2 text-[10px] text-white/80 bg-white/10 rounded-t-lg border-r border-white/10 flex items-center gap-2"
              style={{
                x: combinedTab2X,
                opacity: combinedTab2Opacity,
              }}
            >
              <div className="w-2 h-2 bg-white/10 rounded-full border border-white/10"></div>
              <div className="h-2 bg-white/40 rounded w-8"></div>
            </motion.div>
          </div>

          {/* Browser Content Area */}
          <div className="p-3 relative" style={{ height: "calc(100% - 100px)" }}>
            {/* Tab 1 Content - Article */}
            <motion.div
              className="absolute inset-3"
              style={{
                opacity: useTransform(() => {
                  const tab2Op = tab2Opacity.get();
                  return 1 - tab2Op;
                }),
              }}
            >
              {/* Article Title */}
              <motion.div
                className="bg-white/10 border border-white/10 rounded p-4 mb-4"
                style={{
                  x: combinedTitleX,
                  opacity: useTransform(() => {
                    const combinedOpacity = combinedTitleOpacity.get();
                    const tab2Op = tab2Opacity.get();
                    return combinedOpacity * (1 - tab2Op);
                  }),
                }}
              >
                <div className="h-6 bg-white/10 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-white/10 rounded w-1/2"></div>
              </motion.div>

              {/* Article Paragraph */}
              <motion.div
                className="bg-white/10 border border-white/20 rounded p-4 mb-4"
                style={{
                  x: combinedParagraphX,
                  opacity: useTransform(() => {
                    const combinedOpacity = combinedParagraphOpacity.get();
                    const tab2Op = tab2Opacity.get();
                    return combinedOpacity * (1 - tab2Op);
                  }),
                }}
              >
                <div className="space-y-2">
                  <div className="h-3 bg-white/30 rounded w-full"></div>
                  <div className="h-3 bg-white/30 rounded w-5/6"></div>
                  <div className="h-3 bg-white/30 rounded w-4/6"></div>
                  <div className="h-3 bg-white/30 rounded w-3/4"></div>
                  <div className="h-3 bg-white/30 rounded w-5/6"></div>
                </div>
              </motion.div>

              {/* Article Image */}
              <motion.div
                className="bg-white/10 border border-white/10 rounded p-4"
                style={{
                  x: combinedImageX,
                  opacity: useTransform(() => {
                    const combinedOpacity = combinedImageOpacity.get();
                    const tab2Op = tab2Opacity.get();
                    return combinedOpacity * (1 - tab2Op);
                  }),
                }}
              >
                <div className="h-24 bg-white/10 rounded"></div>
              </motion.div>
            </motion.div>

            {/* Tab 2 Content - Form */}
            <motion.div
              className="absolute inset-3"
              style={{
                opacity: tab2Opacity,
              }}
            >
              {/* Form Title */}
              <motion.div
                className="bg-white/10 border border-white/10 rounded p-3 mb-3"
                style={{
                  x: formTitleExitX,
                  opacity: combinedFormTitleOpacity,
                }}
              >
                <div className="h-5 bg-white/10 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-white/10 rounded w-1/2"></div>
              </motion.div>

              {/* Form Input 1 */}
              <motion.div
                className="bg-white/10 border border-white/20 rounded p-3 mb-3"
                style={{
                  x: formInput1ExitX,
                  opacity: combinedFormInput1Opacity,
                }}
              >
                <div className="h-2 bg-white/40 rounded w-1/4 mb-2"></div>
                <div className="h-8 bg-white/5 rounded border border-white/30 mb-1 px-3 flex items-center relative shadow-inner focus-within:border-orange-400/50">
                  {/* Input placeholder */}
                  <motion.div
                    className="h-2 bg-white/20 rounded w-16 absolute left-3"
                    style={{
                      opacity: useTransform(() => {
                        const width = typingTextWidth.get();
                        return width > 10 ? 0 : 1;
                      }),
                    }}
                  />
                  {/* Typing text - simulating character by character */}
                  <div className="flex relative z-10">
                    <motion.div
                      className="h-2 bg-white/90 rounded mr-0.5"
                      style={{
                        width: useTransform(() => {
                          const progress = scrollYProgress.get();
                          if (progress >= 0.45) {
                            const width = typingTextWidth.get();
                            return Math.min(width, 80);
                          }
                          return 0;
                        }),
                      }}
                    />
                    {/* Typing cursor */}
                    <motion.div
                      className="w-0.5 h-3 bg-white/10"
                      style={{
                        opacity: useTransform(() => {
                          const visible = typingCursorVisible.get();
                          return visible;
                        }),
                      }}
                      animate={{
                        opacity: [1, 0, 1],
                      }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                  </div>
                </div>
                <div className="h-2 bg-white/10 rounded w-3/4"></div>
              </motion.div>

              {/* Form Input 2 */}
              <motion.div
                className="bg-white/10 border border-white/20 rounded p-3 mb-3"
                style={{
                  x: formInput2ExitX,
                  opacity: combinedFormInput2Opacity,
                }}
              >
                <div className="h-2 bg-white/40 rounded w-1/4 mb-2"></div>
                <div className="h-8 bg-white/5 rounded border border-white/30 mb-1 px-3 flex items-center relative shadow-inner">
                  {/* Input placeholder */}
                  <div className="h-2 bg-white/20 rounded w-20"></div>
                </div>
                <div className="h-2 bg-white/10 rounded w-1/2"></div>
              </motion.div>

              {/* Form Button */}
              <motion.div
                className="bg-white/10 border border-white/10 rounded px-6 py-3 w-fit"
                style={{
                  x: formButtonExitX,
                  scale: buttonPressScale,
                  opacity: combinedFormButtonOpacity,
                }}
              >
                <div className="h-3 bg-white/10 rounded w-16"></div>
              </motion.div>
            </motion.div>

            {/* Mouse Cursor */}
            <motion.div
              className="absolute w-4 h-6 pointer-events-none z-10"
              style={{
                x: useTransform(() => {
                  const progress = scrollYProgress.get();
                  if (progress < 0.3) {
                    return mouseAppearX.get(); // 出现动画
                  } else if (progress < 0.38) {
                    return 200; // 滚动时保持X位置
                  } else if (progress < 0.52) {
                    return mouseTabX.get(); // 移动到Tab
                  } else if (progress < 0.58) {
                    return mouseButtonX.get(); // 移动到Button
                  } else if (progress < 0.68) {
                    return 120; // 在button位置等待
                  } else {
                    return 120 + mouseFinalX.get(); // 最终右移
                  }
                }),
                y: useTransform(() => {
                  const progress = scrollYProgress.get();
                  if (progress < 0.3) {
                    return 80; // 出现时的初始Y位置
                  } else if (progress < 0.38) {
                    return mouseScrollY.get(); // 滚动时向下
                  } else if (progress < 0.52) {
                    return mouseTabY.get(); // 移动到Tab
                  } else if (progress < 0.58) {
                    return mouseButtonY.get(); // 移动到Button
                  } else {
                    return 280; // 保持Y位置不变
                  }
                }),
                rotate: useTransform(() => {
                  const progress = scrollYProgress.get();
                  if (progress < 0.24) {
                    return mouseAppearRotation.get(); // 出现时旋转
                  } else {
                    return 0;
                  }
                }),
                scale: 1,
                opacity: useTransform(() => {
                  const progress = scrollYProgress.get();
                  if (progress < 0.28) {
                    return 0; // 还未出现
                  } else if (progress < 0.68) {
                    return mouseAppearOpacity.get(); // 正常可见
                  } else {
                    return mouseFinalOpacity.get(); // 右移时渐隐
                  }
                }),
              }}
            >
              <svg viewBox="0 0 16 24" fill="none" className="w-full h-full">
                <path d="M0 0L0 16L5 11L8 16L11 14L8 9L16 9L0 0Z" fill="white" stroke="#000" strokeWidth="0.5" />
              </svg>
            </motion.div>

            {/* Scrollbar */}
            <motion.div
              className="absolute right-2 top-20 bottom-4 w-2 bg-white/20 rounded-full"
              style={{
                x: scrollbarExitX,
                opacity: useTransform(() => {
                  const progress = scrollYProgress.get();
                  if (progress < 0.32) {
                    return 0;
                  } else if (progress < 0.68) {
                    return scrollbarAppearOpacity.get();
                  } else {
                    return scrollbarExitOpacity.get();
                  }
                }),
              }}
            >
              <motion.div
                className="w-full bg-white/10 rounded-full"
                style={{
                  height: useTransform(() => {
                    const progress = scrollYProgress.get();
                    if (progress < 0.32) {
                      return 8;
                    } else if (progress < 0.68) {
                      return scrollbarActiveHeight.get();
                    } else {
                      return 20;
                    }
                  }),
                }}
              ></motion.div>
            </motion.div>

            {/* Background pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
              <div className="absolute top-4 right-4 w-8 h-8 border border-white/10 rounded"></div>
              <div className="absolute bottom-8 left-8 w-4 h-4 border border-white/10 rounded-full"></div>
              <div className="absolute top-1/2 left-4 w-2 h-2 border border-white/10 rounded-full"></div>
            </div>

            {/* Interaction Indicators */}

            {/* Button Click Indicator - 橙色 */}
            <motion.div
              className="absolute w-8 h-8 border-2 border-white/10 rounded-full pointer-events-none z-10"
              style={{
                x: 30, // 按钮位置
                y: 280, // 按钮高度
                opacity: buttonClickIndicatorOpacity,
                scale: useTransform(buttonClickIndicatorOpacity, [0, 1], [0.5, 1.5]),
              }}
            />
          </div>

          {/* Loading Indicator - 与动画同步扩展到0.7 */}
          <motion.div
            className="absolute top-0 left-0 right-0 h-1 bg-white/10 rounded-t-lg"
            style={{
              scaleX: useTransform(scrollYProgress, [0.1, 0.68], [0, 1]),
              transformOrigin: "left",
            }}
          />
        </motion.div>
      </div>
  );
}
